import React, { useEffect, useRef, useState } from "react";
import { parsePhoneNumberFromString, isValidPhoneNumber, AsYouType } from 'libphonenumber-js';

import useForm from "../../hooks/useForm";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import showToast from "../../utils/toastNotifications";
import { COUNTRY_CODES } from "../../constants/commonConstants";

import CustomInput from "../../components/Form/Input";
import CustomButton from "../../components/Form/CustomButton";
import FileUploadInput from "../../components/Form/FileUploadInput";
import DropDownNew from "../../components/Form/DropDownNew";
import { FORM_MODES, firebaseUplaodFolders, maximumImagesUploadingSizes } from "../../constants/commonConstants";
import { useThunk } from "../../hooks/useThunk";
import {addTenantData, editTenantData, fetchUnitData, deleteTenant, fetchPrevRatings} from "../../store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";
import PreviousRatingsModal from "../../components/Model/PreviousRatingsModal";

const formatInternationalPhoneNumber = (input) => {
  // Remove any existing formatting first
  let cleanInput = input.replace(/[^\d+]/g, '');

  // If input doesn't start with +, and has digits, assume it needs country code
  if (cleanInput && !cleanInput.startsWith('+')) {
    cleanInput = '+' + cleanInput;
  }

  // Use AsYouType for real-time formatting
  const formatter = new AsYouType();
  const formatted = formatter.input(cleanInput);
  // console.log("formatter", formatted);

  return formatted;
};

const validateInternationalPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.trim().length < 0) {
    return "Phone number is required";
  }

  if (!isValidPhoneNumber(phoneNumber)) {
    return "Please enter a valid phone number";
  }

  try {
    const parsed = parsePhoneNumberFromString(phoneNumber);

    if (!parsed) {
      return "Please enter a valid phone number with country code";
    }

    if (!parsed.isValid()) {
      return "Please enter a valid phone number";
    }

    return null; // Valid
  } catch (error) {
    return "Please enter a valid international phone number";
  }
};

const notificationOptions = [
  { label: "Email", value: "Email" },
];
const notificationOptionsWithSMS = [
  { label: "Email", value: "Email" },
  { label: "SMS", value: "SMS" },
];

const Tenantdetailsform = ({ mode, unitId, propertyId }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [tenantData, setTenantData] = useState();
  const [selectedProperty, setSelectedProperty] = useState();
  const [selectedUnit, setSelectedUnit] = useState();
  const [requiredEmail, setRequiredEmail] = useState(true);
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [ratingsOpened, setRatingsOpened] = useState(false);
  const [isPrevRatingsOpen, setIsPrevRatingsOpen] = useState(false);
  const [prevRatings, setPrevRatings] = useState([]);
  const [isLoadingPrevRatings, setIsLoadingPrevRatings] = useState(false);
  const [entryDateError, setEntryDateError] = useState(""); // calender only today .. +3 months
  const navigate = useNavigate();

  const properties = useSelector((state) => state.property.properties);

  const [doFetchUnitData, isFetchingUnitData, fetchUnitDataError] = useThunk(fetchUnitData);
  const [doEditTenant, isEditingTenant, editTenantError] = useThunk(editTenantData);
  const [doAddTenant, isAddingTenant, addTenantError] = useThunk(addTenantData);
  const [doFetchPrevRatings, isFetchPrevRatings] = useThunk(fetchPrevRatings);
  const [doDeleteTenant, isDeletingTenant, deleteTenantError] = useThunk(deleteTenant);
  const [doUploadRefrencesToFirebase, isUploadRefrencesToFirebaseLoading, uploadRefrencesToFirebaseError] = useFirebaseFileUpload();

  useEffect(() => {
    const fetchUnitData = async () => {
      const property = properties.find(property => property._id === propertyId);

      if (property) {
        // console.log('selected property', property);
        setSelectedProperty(property);
      }

      const result = await doFetchUnitData({ unitId: unitId });
      if (result.success) {
        setTenantData(result?.response?.data?.tenant);
        setSelectedUnit(result?.response?.data);
        // console.log('selected unit', result?.response?.data);

        const tenant = result?.response?.data?.tenant;

        // Safe date conversion function
        const formatDateForInput = (dateValue) => {
          if (!dateValue) return "";
          const date = new Date(dateValue);
          // Check if the date is valid
          if (isNaN(date.getTime())) {
            return "";
          }
          return toISODateInput(date);
        };

        setFormData({
          tenantName: tenant?.name || "",
          email: tenant?.email || "",
          phone: tenant?.phoneNumber || "",
          address: tenant?.address || "",
          notification: {
            value: tenant?.notificationPreferences || "Email",
            label: tenant?.notificationPreferences || "Email",
          },
          comments: tenant?.comments || "",
          documents: tenant?.documents || [],
          entryDate: formatDateForInput(result?.response?.data?.tenantStartDate),
        });

      }
    }

    fetchUnitData();
  }, [doFetchUnitData, mode, unitId]);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
    setFormData
  } = useForm(
    {
      tenantName: "",
      email: "",
      phone: "",
      address: "",
      notification: { value: "Email", label: "Email" },
      comments: "",
      documents: null,
      entryDate: "",
    },
    {
      tenantName: true,
      email: requiredEmail,
      phone: true,
      address: true,
      notification: true,
      comments: false,
      documents: false,
      entryDate: true,
    },
    {
      tenantName: COMMON_FIELD_TYPES.FULLNAME,
      email: COMMON_FIELD_TYPES.EMAIL_FIELD,
      phone: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      address: COMMON_FIELD_TYPES.ADDRESS,
      notification: COMMON_FIELD_TYPES.DROPDOWN,
      comments: COMMON_FIELD_TYPES.TEXT_FIELD,
      documents: COMMON_FIELD_TYPES.TEXT_FIELD,
      entryDate: COMMON_FIELD_TYPES.REQUIRED_FIELD,
    }
  );

  useEffect(() => {
    if (
        !ratingsOpened &&
        formData?.email?.trim() !== "" &&
        formData?.phone?.trim() !== ""
    ) {
      setRatingsOpened(true);
    }
  }, [formData.email, formData.phone, ratingsOpened]);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index, isExisting = false) => {
    if (isExisting) {
      setFormData(prev => ({
        ...prev,
        documents: prev.documents.filter((_, i) => i !== index)
      }));
    } else {
      setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleValidationError = (invalidFiles) => {
    showToast("error", "Some files are invalid. Please check the file types and sizes.");
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/"); // change to a safe default route in your app
  };

  const openPreviousRatings = async () => {
    const email = (formData.email || "").trim();
    const phoneNumber = formData.phone?.trim()
    if (!email) {
      showToast("error", "Please enter an email first.");
      return;
    }
    if (!phoneNumber) {
      showToast("error", "Please enter a phone number first.");
      return;
    }
    setIsLoadingPrevRatings(true);
    setPrevRatings([]);
    const res = await doFetchPrevRatings({ email, phoneNumber });
    setIsLoadingPrevRatings(false);

    if (res.success === true) {
      setPrevRatings(res.response.data);
      setIsPrevRatingsOpen(true);
    }else{
      showToast("error", res?.error.message ? res?.error.message : "Unable to fetch ratings");
    }
  };

  // --- Date helpers (min=today, max=today+3 months) --- // calender only today .. +3 months
  const toISODateInput = (d) => d.toISOString().slice(0, 10); // YYYY-MM-DD

  const getTodayISO = () => {
    const d = new Date();
    // normalize to local date (strip time)
    d.setHours(0, 0, 0, 0);
    return toISODateInput(d);
  };

  const getMax3MonthsISO = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setMonth(d.getMonth() + 3);
    return toISODateInput(d);
  };

  const isWithinNext3Months = (val) => {
    if (!val) return false;
    const chosen = new Date(val + 'T00:00:00');
    const min = new Date(getTodayISO() + 'T00:00:00');
    const max = new Date(getMax3MonthsISO() + 'T00:00:00');
    return chosen >= min && chosen <= max;
  };

  return (
    <div className="p-4 sm:p-6 md:p-[30px]">
      <header className="flex justify-between items-center bg-white p-4 rounded-lg shadow-md h-[87px]">
        <div className="flex items-center space-x-2">
          <span className="text-xs font-semibold text-blue-600 sm:text-sm md:text-base lg:text-lg">
            {selectedProperty?.propertyName}
          </span>
          <span className="text-[#113092] font-bold text-xs sm:text-sm md:text-xl lg:text-2xl">
            {selectedUnit?.unitId}
          </span>
          <span className="text-[#113092] font-bold text-xs sm:text-sm md:text-lg lg:text-xl">
            {selectedUnit?.unitName && selectedUnit?.unitName !== "-" ? selectedUnit?.unitName : ""}
          </span>
        </div>
        {/* {mode === FORM_MODES.EDIT && (
          <CustomButton
            buttonText="Remove Tenant"
            bgColor="bg-white"
            textColor="text-red-500"
            borderColor="border-red-500"
            textWeight="font-normal"
            className="
                px-2 sm:px-0 md:px-0 py-1 sm:py-1.5 md:py-2
                h-[26px] sm:h-[34px] md:h-[42px] lg:h-[45px]
                w-[100px] sm:w-[120px] md:w-[142px] lg:w-[155px] min-w-[100px]
                rounded-[8px]
                hover:bg-red-200 transition
                text-xs sm:text-sm md:text-base lg:text-lg
              "
            // className="border border-red-500 text-red-500 font-normal px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-[8px] hover:bg-red-100 transition text-xs sm:text-sm md:text-base lg:text-lg"
            onClick={handleDeleteTenant}
            loading={isDeletingTenant}
            disabled={isDeletingTenant}
          />
        )}*/}
      </header>

      <div className="w-full p-6 mx-auto mt-10 bg-white shadow-md rounded-xl sm:p-8 ">
        <div
          className="flex flex-col gap-y-4 sm:gap-y-6"
        // onSubmit={handleSubmit}
        >
          {/* Tenant Name */}
          <CustomInput
            label={
              <span className="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                Tenant Name 
              </span>
            }
            type="text"
            name="tenantName"
            value={formData.tenantName}
            placeholder="Enter tenant name"
            error={touched.tenantName && errors.tenantName}
            onChange={(e) => handleInputChange("tenantName", e.target.value)}
            onBlur={() => handleBlur("tenantName", formData.tenantName)}
            onFocus={() => clearError("tenantName")}
            labelStyle="text-[#09114B] text-lg font-inter font-medium"
            inputStyle="text-xs sm:text-sm md:text-base lg:text-lg"
            className="
              inline-flex items-center flex-shrink-0 
              border border-gray-300 
              bg-primary-light/30 text-content 
              placeholder:text-[#9D9D9D]
              h-8 sm:h-9 md:h-10 lg:h-[45px] rounded-[5px] px-[13px] py-[14px]
              w-full min-w-[120px]
            "
            disabled={true}
          />

          {/* Email and Phone */}
          <div className="flex flex-col md:flex-row gap-y-4 md:gap-6">
            <CustomInput
              label={
                <span className="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                  Email Address
                </span>
              }
              type="email"
              name="email"
              value={formData.email}
              placeholder="Enter email address"
              error={touched.email && errors.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={() => handleBlur("email", formData.email)}
              onFocus={() => clearError("email")}
              labelStyle="text-[#09114B] text-lg font-inter font-medium"
              inputStyle="text-xs sm:text-sm md:text-base lg:text-lg"
              className="
                inline-flex items-center flex-shrink-0 
                text-content w-full min-w-[120px]
                bg-primary-light/30 
                placeholder:text-[#9D9D9D]
                h-8 sm:h-9 md:h-10 lg:h-[45px] 
                py-[6px] px-[13px]
                rounded-[5px] 
                border border-[rgba(129,129,129,0.5)]
              "
              disabled={true}
            />

            {/* Phone number */}
            <div className="w-full min-w-[120px]">
              <div className="flex items-center justify-between">
                  <span className="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                    Phone Number
                  </span>
                {ratingsOpened &&
                    <button
                        type="button"
                        onClick={openPreviousRatings}
                        className="
                         text-[8px] sm:text-[9px] md:text-[10px] lg:text-[11px] xl:text-[12px]
                          text-[#113092] hover:text-[#0b1f6f]
                          underline underline-offset-2 decoration-[#113092]/50
                          font-medium
                        "
                    >
                      Previous Ratings on Tenafix
                    </button>
                }
              </div>
              <div className="flex items-center w-full gap-2 mt-1 sm:mt-2">
                <CustomInput
                    type="tel"
                    name="phone"
                    placeholder="Phone number"
                    value={formData.phone}
                    error={touched.phone && phoneNumberError}
                    onChange={(e) => {
                      const masked = formatInternationalPhoneNumber(e.target.value);
                      handleInputChange("phone", masked);
                      setPhoneNumberError(validateInternationalPhoneNumber(masked));
                    }}
                    onBlur={() => {
                      handleBlur("phone", formData.phone);
                      setPhoneNumberError(validateInternationalPhoneNumber(formData.phone));
                      if (!formData.phone?.startsWith('+256')) {
                        setFormData(prev => ({
                          ...prev,
                          notification: {value: "Email", label: "Email"}
                        }));
                        setRequiredEmail(true);
                      }
                    }}
                    onFocus={() => clearError("phone")}
                    className={`
                    flex-grow w-full min-w-[100px]
                    h-8 sm:h-9 md:h-10 lg:h-[45px]
                    px-4 bg-white rounded-[5px] text-content focus:outline-none focus:ring-2 focus:ring-white
                    text-xs sm:text-sm md:text-base lg:text-lg
                    placeholder:text-[#9D9D9D]
                    border ${touched.phone && errors.phone
                        ? "border-red-500"
                        : "border-[#81818180]"
                    }
                `}
                    disabled={true}
                />
              </div>
            </div>
          </div>

          {/* Tenant Address */}
          <CustomInput
              label={
                <span className="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                Tenant Address 
              </span>
              }
              type="text"
              name="address"
              value={formData.address}
              placeholder="Enter tenant address"
              error={touched.address && errors.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              onBlur={() => handleBlur("address", formData.address)}
              onFocus={() => clearError("address")}
              labelStyle="text-[#09114B] text-lg font-inter font-medium"
              className={`
              text-content bg-white
              placeholder:text-[#9D9D9D]
              w-full min-w-[120px]
              h-8 sm:h-9 md:h-10 lg:h-[45px]
              rounded-[5px]
              py-[6px] px-[13px]
              text-xs sm:text-sm md:text-base lg:text-lg
              ${touched.address && errors.address
                ? "border border-red-400"
                : "border border-[rgba(129,129,129,0.5)]"
              }
            `}
            disabled={true}
          />

          {/* Notification Preference */}
          <div className="flex flex-col md:flex-row gap-y-4 md:gap-6">

            <DropDownNew
              label="Notification Preference"
              labelStyle="
                block mb-2
                text-[#09114B]
                text-xs sm:text-sm md:text-base lg:text-lg
                font-inter font-medium "
              name="notification"
              isRequired={false}
              disabled={true}
              error={touched.notification && errors.notification}
              options={formData.phone?.startsWith('+256') ? notificationOptionsWithSMS : notificationOptions}
              defaultOption={formData.notification}
              /* onSelect={(value) => {
                 handleInputChange("notification", value);
                 if (value.value === "Email") {
                   setRequiredEmail(true);
                 } else {
                   setRequiredEmail(false);
                 }
               }}*/
              onSelect={() => { }}
              placeholder="Select a preference"
              displayKey="label"
              idKey="value"
              buttonClassName={`
                inline-flex items-center flex-shrink-0
                bg-primary-light/30 text-content
                border-gray-300 rounded-[4px]
                h-8 sm:h-9 md:h-10 lg:h-[45px]
                px-[13px] w-full min-w-[120px]
                text-xs sm:text-sm md:text-base lg:text-lg
              `}
              dropdownClassName="
                w-full relative mt-1 rounded-[4px]
                bg-white border border-gray-300 shadow-md
                z-10 min-w-[120px]
              "
              optionClassName="
                block px-3 py-2 hover:bg-gray-100
                text-xs sm:text-sm md:text-base lg:text-lg
              "
              selectedValueClassName="
                text-xs sm:text-sm md:text-base lg:text-lg
              "
              placeholderClassName="
                text-[#9D9D9D]
                text-xs sm:text-sm md:text-base lg:text-lg
              "
            />
            {/* Entry Date (MM/DD/YYYY, only today .. +3 months) */}
            <div className="w-full min-w-[120px] mt-2">
              <span className="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                When is the tenant entering?
              </span>
              <CustomInput
                type="text"
                name="entryDate"
                placeholder="MM/DD/YYYY"
                value={formData.entryDate}
                error={touched.entryDate && !!entryDateError}
                onChange={(e) => {
                  setEntryDateError("");
                }}        
                onFocus={() => {
                  // don’t clear the value, only the error
                  setEntryDateError("");
                }}
                labelStyle="text-[#09114B] text-lg font-inter font-medium"
                inputStyle="text-xs sm:text-sm md:text-base lg:text-lg"
                className={`
                  mt-1 sm:mt-2 w-full
                  h-8 sm:h-9 md:h-10 lg:h-[45px]
                  px-4 bg-white rounded-[5px] text-content
                  text-xs sm:text-sm md:text-base lg:text-lg
                  placeholder:text-[#9D9D9D]
                  border ${ (touched.entryDate && entryDateError)
                    ? "border-red-500"
                    : "border-[#81818180]"
                  }
                  focus:outline-none focus:ring-2 focus:ring-white
                `}
                            disabled={true}

              />
              {(touched.entryDate && entryDateError) && (
                <p className="text-red-500 text-xs mt-1">{entryDateError}</p>
              )}
            </div>
          </div>

          {/* Comments */}
          <div>
            <CustomInput
              label="Comments (Optional)"
              type="textarea"
              name="comments"
              value={formData.comments}
              onChange={(e) => handleInputChange("comments", e.target.value)}
              onBlur={() => handleBlur("comments", formData.comments)}
              onFocus={() => clearError("comments")}
              labelStyle="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium"
              inputStyle="text-xs sm:text-sm md:text-base lg:text-lg"
              className="
                placeholder:text-[#9D9D9D]
                border border-[rgba(129,129,129,0.5)]
                h-[80px] py-[6px] px-[13px]
                w-full min-w-[120px]
              "
              outerContainerStyle="mt-3"
              disabled={true}
            />
          </div>

          {/* File Upload */}
          <FileUploadInput
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            files={uploadedFiles}
            existingsFiles={formData.documents || []}
            onRemoveFile={handleRemoveFile}
            onValidationError={handleValidationError}
          />

          {/* Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mt-6 sm:gap-5 sm:mt-8">
            {/* <button
              type="button"
              onClick={() => window.location.reload()}
              className="
                h-[30px] sm:h-[45px] md:h-[55px]
                w-[120px] sm:w-[120px] md:w-[120px] min-w-[120px]
                border border-[#4772F1]
                text-[#4772F1] font-bold rounded-[8px]
                hover:bg-[#f0f4ff]
                text-xs sm:text-sm md:text-base lg:text-lg
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              className="
                h-[30px] sm:h-[45px] md:h-[55px]
                w-[120px] sm:w-[150px] md:w-[170px] min-w-[120px]
                bg-[#4772F1] text-white font-bold rounded-[8px]
                hover:bg-[#5c81ef]
                text-xs sm:text-sm md:text-base lg:text-lg
              "
            >
              { mode === FORM_MODES.EDIT ? 'Update' : 'Submit'}
            </button> */}
            <CustomButton
              type="button"
              buttonText="Back"
              bgColor="bg-white"
              textColor="text-blue"
              className="
                h-[30px] sm:h-[45px] md:h-[55px]
                w-[120px] sm:w-[120px] md:w-[120px] min-w-[120px]
                border border-[#4772F1]
                text-[#4772F1] font-bold rounded-[8px]
                hover:bg-[#f0f4ff]
                text-xs sm:text-sm md:text-base lg:text-lg
              "
              // className=" w-full md:w-[220px] h-10 sm:h-14 text-[12px] sm:text-[16px] font-alexandria font-semibold rounded-md border border-blue"
              onClick={handleBack}
              disabled={isAddingTenant || isEditingTenant}
            />
            {/* <CustomButton
              type="button"
              buttonText={`${mode === FORM_MODES.EDIT ? 'Update' : 'Submit'}`}
              bgColor="bg-blue"
              textColor="text-white"
              className="
                h-[30px] sm:h-[45px] md:h-[55px]
                w-[120px] sm:w-[150px] md:w-[170px] min-w-[120px]
                bg-[#4772F1] text-white font-bold rounded-[8px]
                hover:bg-[#5c81ef]
                text-xs sm:text-sm md:text-base lg:text-lg
              "
              // className=" w-full md:w-[220px] h-10 sm:h-14 text-[12px] sm:text-[16px] font-alexandria font-semibold rounded-md"
              onClick={handleSubmit}
              loading={isAddingTenant || isEditingTenant || isUploadRefrencesToFirebaseLoading}
              disabled={isAddingTenant || isEditingTenant}
            />*/}
            <PreviousRatingsModal
                isOpen={isPrevRatingsOpen}
                onClose={() => setIsPrevRatingsOpen(false)}
                tenantEmail={formData.email}
                tenantPhone={formData.phone}
                ratings={prevRatings}
                loading={isLoadingPrevRatings}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tenantdetailsform;
